import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, GitCompareArrows, Minus, Plus, X } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import type {
  EvaluationStatus,
  TechStackAnswer,
  TechStackQuestion,
  TechStackSection,
  TechStackSnapshot,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { StackNow, StackTimeline, type StackReview } from '@/components/stack-timeline'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ErrorNote, Loading, Page } from '@/components/page'
import { useCommitOnUnmount } from '@/hooks/use-unsaved'

interface Detail {
  snapshot: TechStackSnapshot
  sections: TechStackSection[]
  questions: TechStackQuestion[]
  answers: TechStackAnswer[]
  /** This team's other reviews, for the comparison picker. */
  siblings: TechStackSnapshot[]
  /** Answers belonging to `siblings`, for the drift timeline. */
  history: TechStackAnswer[]
}

/**
 * One Tech Stack review: checkboxes, and nothing that belongs to an evaluation.
 *
 * There is no score, no scale legend, no radar and no trend chart here, and that
 * is the point of the feature rather than an omission — see
 * migrations/0021_tech_stack.sql. The comparison against an earlier review is a
 * DIFF (what joined the stack, what left), because that is the only meaningful
 * arithmetic on a set of lists.
 */
export function TechStackDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isLead } = useAuth()
  const [compareId, setCompareId] = React.useState<string>('none')
  const [compareAnswers, setCompareAnswers] = React.useState<TechStackAnswer[]>([])

  const { data, loading, error, refetch, setData } = useQuery<Detail | null>(async () => {
    if (!id) return null
    const { data: snap, error: serr } = await sb()
      .from('tech_stack_snapshots')
      .select('*')
      .eq('id', id)
      .maybeSingle()
    if (serr) throw serr
    if (!snap) return null
    const snapshot = snap as TechStackSnapshot

    const [secs, qs, ans, sib] = await Promise.all([
      sb().from('tech_stack_sections').select('*').order('display_order'),
      sb().from('tech_stack_questions').select('*').order('display_order'),
      sb().from('tech_stack_answers').select('*').eq('snapshot_id', snapshot.id),
      sb()
        .from('tech_stack_snapshots')
        .select('*')
        .eq('team_id', snapshot.team_id)
        .neq('id', snapshot.id)
        .order('taken_on', { ascending: false }),
    ])
    if (secs.error) throw secs.error
    if (qs.error) throw qs.error
    if (ans.error) throw ans.error
    if (sib.error) throw sib.error

    const siblings = (sib.data ?? []) as TechStackSnapshot[]

    // Every review's answers, for the drift timeline. The comparison picker
    // above only ever needs two reviews; the timeline needs all of them, and
    // one query for the lot beats one per review. The whole catalog is 14
    // questions, so even a team with a dozen reviews is a couple of hundred
    // rows.
    let history: TechStackAnswer[] = []
    if (siblings.length > 0) {
      const { data: h, error: herr } = await sb()
        .from('tech_stack_answers')
        .select('*')
        .in('snapshot_id', siblings.map((s) => s.id))
      if (herr) throw herr
      history = (h ?? []) as TechStackAnswer[]
    }

    return {
      snapshot,
      sections: (secs.data ?? []) as TechStackSection[],
      questions: (qs.data ?? []) as TechStackQuestion[],
      answers: (ans.data ?? []) as TechStackAnswer[],
      siblings,
      history,
    }
  }, [id])

  // Default to the most recent earlier review — "what changed" is the question
  // keeping dated snapshots is for.
  React.useEffect(() => {
    if (!data || compareId !== 'none') return
    const earlier = data.siblings.find((s) => s.taken_on <= data.snapshot.taken_on)
    if (earlier) setCompareId(earlier.id)
  }, [data?.snapshot.id]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (compareId === 'none') {
      setCompareAnswers([])
      return
    }
    let active = true
    void sb()
      .from('tech_stack_answers')
      .select('*')
      .eq('snapshot_id', compareId)
      .then(({ data: a }) => {
        if (active) setCompareAnswers((a ?? []) as TechStackAnswer[])
      })
    return () => {
      active = false
    }
  }, [compareId])

  const answerByQuestion = React.useMemo(() => {
    const m = new Map<string, TechStackAnswer>()
    for (const a of data?.answers ?? []) m.set(a.question_id, a)
    return m
  }, [data?.answers])

  const beforeByQuestion = React.useMemo(() => {
    const m = new Map<string, string[]>()
    for (const a of compareAnswers) m.set(a.question_id, a.choices)
    return m
  }, [compareAnswers])

  /**
   * Write an answer. Upsert on (snapshot_id, question_id) so ticking a second
   * box corrects the row rather than adding one, with the local patch applied
   * first — a room working through fourteen questions shouldn't wait on a round
   * trip per checkbox.
   */
  async function setAnswer(question: TechStackQuestion, choices: string[], notes?: string) {
    if (!data) return
    const existing = answerByQuestion.get(question.id)
    const optimistic: TechStackAnswer = {
      id: existing?.id ?? `pending-${question.id}`,
      snapshot_id: data.snapshot.id,
      question_id: question.id,
      team_id: data.snapshot.team_id,
      choices,
      notes: notes ?? existing?.notes ?? null,
      answered_by: existing?.answered_by ?? null,
    }
    setData((prev) =>
      prev
        ? {
            ...prev,
            answers: [...prev.answers.filter((a) => a.question_id !== question.id), optimistic],
          }
        : prev,
    )

    const { error: err } = await sb().from('tech_stack_answers').upsert(
      {
        snapshot_id: data.snapshot.id,
        question_id: question.id,
        team_id: data.snapshot.team_id,
        choices,
        notes: optimistic.notes,
      },
      { onConflict: 'snapshot_id,question_id' },
    )
    if (err) {
      window.alert(err.message)
      void refetch()
    }
  }

  async function setStatus(status: EvaluationStatus) {
    if (!data) return
    const { error: err } = await sb()
      .from('tech_stack_snapshots')
      .update({ status })
      .eq('id', data.snapshot.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  async function remove() {
    if (!data) return
    if (!window.confirm(`Delete “${data.snapshot.name}”? Its answers go with it.`)) return
    const { error: err } = await sb().from('tech_stack_snapshots').delete().eq('id', data.snapshot.id)
    if (err) return window.alert(err.message)
    navigate('/evaluations')
  }

  if (loading && !data) {
    return (
      <Page title="Tech stack">
        <Loading />
      </Page>
    )
  }
  if (error) {
    return (
      <Page title="Tech stack">
        <ErrorNote error={error} />
      </Page>
    )
  }
  if (!data) {
    return (
      <Page title="Review not found">
        <Button variant="outline" asChild>
          <Link to="/evaluations">
            <ArrowLeft /> Back
          </Link>
        </Button>
      </Page>
    )
  }

  const { snapshot, sections, questions, siblings, history } = data

  // Every review of this team's stack, oldest first, each as questionId ->
  // choices. This review's own answers come from `answerByQuestion` because they
  // are edited live on this page; the rest come from the history fetch.
  const timeline: StackReview[] = [...siblings, snapshot]
    .slice()
    .sort((a, b) => a.taken_on.localeCompare(b.taken_on))
    .map((s) => {
      const choices = new Map<string, string[]>()
      if (s.id === snapshot.id) {
        for (const q of questions) {
          const a = answerByQuestion.get(q.id)
          if (a) choices.set(q.id, a.choices)
        }
      } else {
        for (const a of history) if (a.snapshot_id === s.id) choices.set(a.question_id, a.choices)
      }
      return { id: s.id, name: s.name, on: s.taken_on, choices }
    })

  const questionRefs = questions.map((q) => ({ id: q.id, sectionId: q.section_id }))
  const sectionNameOf = (sectionId: string) =>
    sections.find((s) => s.id === sectionId)?.name ?? 'Other'

  const timelineShown = timeline.length > 2
  const answered = questions.filter((q) => (answerByQuestion.get(q.id)?.choices.length ?? 0) > 0).length
  const pct = questions.length === 0 ? 0 : Math.round((answered / questions.length) * 100)
  const comparison = siblings.find((s) => s.id === compareId) ?? null

  // The timeline and the "Since …" card answer different questions — every
  // consecutive step, versus this review against ONE other one you chose. They
  // only collide in the default selection, where "one other" IS the previous
  // review and the two render an identical set of chips a few inches apart.
  //
  // So the timeline wins that case, and the card returns the moment you pick a
  // review further back — which is when it starts saying something the timeline
  // cannot: the CUMULATIVE change, skipping the steps in between.
  const idxOfThis = timeline.findIndex((r) => r.id === snapshot.id)
  const previousReview = idxOfThis > 0 ? timeline[idxOfThis - 1] : null
  const comparisonIsPrevious = Boolean(comparison && previousReview?.id === comparison.id)

  // What joined the stack and what left it, across every question at once. The
  // whole point of keeping dated reviews rather than one current-state page.
  const movement = comparison
    ? questions.reduce(
        (acc, q) => {
          const now = answerByQuestion.get(q.id)?.choices ?? []
          const before = beforeByQuestion.get(q.id) ?? []
          // An unanswered question is not the same as an empty one: comparing
          // against a review that skipped a line would otherwise report every
          // current answer as newly added.
          if (!beforeByQuestion.has(q.id)) return acc
          for (const c of now) if (!before.includes(c)) acc.added.push(c)
          for (const c of before) if (!now.includes(c)) acc.removed.push(c)
          return acc
        },
        { added: [] as string[], removed: [] as string[] },
      )
    : null

  return (
    <Page
      title={snapshot.name}
      description={`Tech stack review · ${formatDate(snapshot.taken_on)}${
        snapshot.facilitator ? ` · ${snapshot.facilitator}` : ''
      }`}
      actions={
        <>
          <Select value={snapshot.status} onValueChange={(v) => void setStatus(v as EvaluationStatus)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
          {isLead && (
            <Button variant="ghost" onClick={() => void remove()} aria-label="Delete review">
              <X />
            </Button>
          )}
        </>
      }
    >
      <Button variant="ghost" size="sm" className="mb-4 -ml-2" asChild>
        <Link to="/evaluations">
          <ArrowLeft /> Evaluations
        </Link>
      </Button>

      <Card className="mb-5">
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4">
          <div className="min-w-[200px] flex-1">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">
                {answered} of {questions.length} answered
              </span>
              <span className="text-xs text-muted-foreground">{pct}%</span>
            </div>
            <Progress value={pct} className="mt-1.5 h-1.5" />
          </div>
          {siblings.length > 0 && (
            <div className="flex items-center gap-2">
              <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
              <Select value={compareId} onValueChange={setCompareId}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Compare with…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No comparison</SelectItem>
                  {siblings.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} · {formatDate(s.taken_on)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* The whole stack on one screen, before the fourteen questions that
          produced it. Assembling this by scrolling the sections below is work
          the reader shouldn't have to do to answer "what do they run on". */}
      {answered > 0 && (
        <Card className="mb-5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">The stack now</CardTitle>
          </CardHeader>
          <CardContent>
            <StackNow
              choices={new Map(questions.map((q) => [q.id, answerByQuestion.get(q.id)?.choices ?? []]))}
              questions={questionRefs}
              sections={sections}
            />
          </CardContent>
        </Card>
      )}

      {/* Three reviews, not two. With exactly two, this timeline has a single
          entry that says precisely what the "Since …" card below already says,
          and stacking the same diff twice reads as a bug. The timeline earns its
          place once there is an ARC — where the pairwise card can only ever
          answer one comparison at a time. */}
      {timeline.length > 2 && (
        <Card className="mb-5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">How the stack changed</CardTitle>
            <CardDescription>
              Every review, newest first — what joined and what left since the one before. There is
              no score here on purpose: a stack is a set of choices, not a number that goes up.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StackTimeline
              reviews={timeline}
              questions={questionRefs}
              sectionName={sectionNameOf}
            />
          </CardContent>
        </Card>
      )}

      {!(timelineShown && comparisonIsPrevious) &&
        movement &&
        (movement.added.length > 0 || movement.removed.length > 0) && (
        <Card className="mb-5 border-primary/30 bg-primary/[0.03]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Since {comparison?.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-1.5 pb-4">
            {movement.added.map((c) => (
              <Badge key={`+${c}`} variant="success" className="gap-1 text-[11px]">
                <Plus className="h-3 w-3" /> {c}
              </Badge>
            ))}
            {movement.removed.map((c) => (
              <Badge key={`-${c}`} variant="muted" className="gap-1 text-[11px] line-through">
                <Minus className="h-3 w-3" /> {c}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="space-y-5">
        {sections.map((section) => {
          const own = questions.filter((q) => q.section_id === section.id)
          if (own.length === 0) return null
          return (
            <Card key={section.id}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{section.name}</CardTitle>
                {section.description && (
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {own.map((q) => (
                  <QuestionBlock
                    key={q.id}
                    question={q}
                    answer={answerByQuestion.get(q.id) ?? null}
                    before={comparison ? beforeByQuestion.get(q.id) ?? null : null}
                    onChange={(choices) => void setAnswer(q, choices)}
                    onNotes={(notes) =>
                      void setAnswer(q, answerByQuestion.get(q.id)?.choices ?? [], notes)
                    }
                  />
                ))}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </Page>
  )
}

/**
 * One question: its offered options as checkboxes, plus a box for whatever the
 * catalog didn't anticipate.
 *
 * Two decisions worth keeping:
 *
 * - **Typed-in answers become checkboxes too**, appended after the offered ones
 *   and removable, so a stack running F# reads the same as one running C#. The
 *   options are a prompt, not an enum — the column is `text[]` for this reason.
 * - **Saved order follows the catalog, not click order**, so two teams that
 *   ticked the same boxes store the same list and a diff is a diff rather than
 *   set arithmetic.
 */
function QuestionBlock({
  question,
  answer,
  before,
  onChange,
  onNotes,
}: {
  question: TechStackQuestion
  answer: TechStackAnswer | null
  /** The same question's answer in the review being compared against, if any. */
  before: string[] | null
  onChange: (choices: string[]) => void
  onNotes: (notes: string) => void
}) {
  const [adding, setAdding] = React.useState('')
  const selected = answer?.choices ?? []
  const chosen = new Set(selected)
  const custom = selected.filter((s) => !question.options.includes(s))

  const commit = (next: string[]) =>
    onChange([
      ...question.options.filter((o) => next.includes(o)),
      ...next.filter((s) => !question.options.includes(s)),
    ])

  const toggle = (value: string) =>
    commit(chosen.has(value) ? selected.filter((s) => s !== value) : [...selected, value])

  const add = () => {
    const value = adding.trim()
    setAdding('')
    if (!value) return
    // Case-insensitive, so "azure" doesn't become a second Azure.
    if (selected.some((s) => s.toLowerCase() === value.toLowerCase())) return
    const match = question.options.find((o) => o.toLowerCase() === value.toLowerCase())
    toggle(match ?? value)
  }

  return (
    <div>
      <p className="text-sm font-medium">{question.name}</p>
      {question.prompt && <p className="mt-0.5 text-xs text-muted-foreground">{question.prompt}</p>}

      <div className="mt-2 flex flex-wrap gap-1.5">
        {[...question.options, ...custom].map((o) => {
          const on = chosen.has(o)
          const isCustom = !question.options.includes(o)
          // Only meaningful while a comparison is selected, and only on a
          // question the earlier review actually answered.
          const wasThere = before?.includes(o)
          const joined = on && before != null && !wasThere
          const left = !on && wasThere
          return (
            <span key={o} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => toggle(o)}
                role="checkbox"
                aria-checked={on}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  on
                    ? 'border-primary bg-primary/10 font-medium text-foreground'
                    : 'border-input text-muted-foreground hover:bg-accent',
                  left && 'line-through opacity-70',
                  isCustom && on && 'rounded-r-none border-r-0',
                )}
              >
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border',
                    on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {on && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                </span>
                {o}
                {joined && <span className="text-[10px] font-semibold text-primary">new</span>}
              </button>
              {isCustom && on && (
                <button
                  type="button"
                  onClick={() => onChange(selected.filter((s) => s !== o))}
                  aria-label={`Remove ${o}`}
                  className="rounded-r-md border border-l-0 border-primary bg-primary/10 py-1.5 pl-1 pr-2 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          )
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          onBlur={add}
          placeholder="Something else? Type it and press Enter"
          className="h-8 max-w-xs text-xs"
        />
      </div>

      <NoteField
        key={`${question.id}-note`}
        initial={answer?.notes ?? ''}
        onCommit={onNotes}
      />
    </div>
  )
}

/**
 * The detail a checkbox can't hold — versions, which service runs what. Saves on
 * blur rather than per keystroke: one upsert per edit, and no debounce timer to
 * leak.
 *
 * And on unmount, because no browser fires `blur` for a focused element that is
 * removed from the DOM — so navigating away mid-sentence used to drop it. The
 * `committed` latch keeps the blur and the unmount from sending it twice.
 */
function NoteField({ initial, onCommit }: { initial: string; onCommit: (notes: string) => void }) {
  const [value, setValue] = React.useState(initial)
  const committed = React.useRef(initial.trim())

  React.useEffect(() => {
    setValue(initial)
    committed.current = initial.trim()
  }, [initial])

  const commit = () => {
    const next = value.trim()
    if (next === committed.current) return
    committed.current = next
    onCommit(next)
  }
  useCommitOnUnmount(commit)

  return (
    <Textarea
      rows={1}
      value={value}
      placeholder="Versions, which service runs what… (optional)"
      className="mt-2 min-h-0 resize-y text-xs"
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
    />
  )
}
